import React from "react";
import { useLocation, Navigate, Routes, Route, useNavigate } from "react-router-dom";
import { ChevronLeft } from "@mui/icons-material";
import BasicLayout from "components/Layouts/BasicLayout";
import LoadingOverlay from "components/LoadingOverlay";
import { NewsGenerateProvider, useNewsGenerate } from "./NewsGenerateContext";
import ConfigureStep from "./ConfigureStep";
import ReviewStep from "./ReviewStep";
import FinalizeStep from "./FinalizeStep";

const NewsGenerateContent: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { loading, generateNews, saveNews, prompt, newsArticle } = useNewsGenerate();
  const currentTab = location.pathname.split('/').pop() || 'configure';

  const steps = ['Configure', 'Review', 'Finalize'];

  const getActiveStep = () => {
    switch (currentTab) {
      case 'configure': return 0;
      case 'review': return 1;
      case 'finalize': return 2;
      default: return 0;
    }
  }

  const handleNext = async () => {
    if (currentTab === 'configure') {
      const success = await generateNews();
      if (success) {
        navigate('/news/generate/review');
      }
    } else if (currentTab === 'review') {
      navigate('/news/generate/finalize');
    } else if (currentTab === 'finalize') {
      const success = await saveNews();
      if (success) {
        navigate('/news');
      }
    }
  };

  const isNextDisabled = () => {
    if (currentTab === 'configure') {
      return !prompt;
    }
    if (currentTab === 'review' || currentTab === 'finalize') {
      return !newsArticle;
    }
    return false;
  };

  return (
    <BasicLayout>
      <div className="w-full">
        {/* Header with back button */}
        <div className="flex items-center mb-8 border-b border-gray-200 pb-4">
          <button
            onClick={() => navigate("/news")}
            className="mr-3 p-2 rounded-full hover:bg-gray-100 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500"
            aria-label="Back to news"
          >
            <ChevronLeft className="h-5 w-5 text-gray-600" />
          </button>
          <h1 className="text-2xl font-bold text-gray-800">Generate News</h1>
        </div>

        {/* Progress Stepper */}
        <div className="mb-10 w-full">
          <h4 className="sr-only">Generation Progress</h4>
          <p className="text-sm font-medium text-gray-900">Generating news content...</p>
          <div aria-hidden="true" className="mt-6">
            <div className="overflow-hidden rounded-full bg-gray-200">
              <div
                style={{ width: `${(getActiveStep() / (steps.length - 1)) * 100}%` }}
                className="h-2 rounded-full bg-blue-600 transition-all duration-300"
              />
            </div>
            <div className="mt-6 hidden grid-cols-3 text-sm font-medium text-gray-600 sm:grid">
              <div className={getActiveStep() >= 0 ? "text-blue-600" : ""}>Configure</div>
              <div className={`text-center ${getActiveStep() >= 1 ? "text-blue-600" : ""}`}>Review</div>
              <div className={`text-right ${getActiveStep() >= 2 ? "text-blue-600" : ""}`}>Finalize</div>
            </div>
          </div>
        </div>

        {/* Main Content Area */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-8 border border-gray-100 min-h-[400px] w-full">
          <Routes>
            <Route path="configure" element={<ConfigureStep />} />
            <Route path="review" element={<ReviewStep />} />
            <Route path="finalize" element={<FinalizeStep />} />
            <Route path="/" element={<Navigate to="configure" replace />} />
          </Routes>
        </div>

        {/* Action Button */}
        <div className="flex justify-end">
          <button
            onClick={handleNext}
            disabled={isNextDisabled()}
            className={`px-6 py-2.5 rounded-md text-white font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 ${isNextDisabled()
              ? 'bg-gray-300 cursor-not-allowed'
              : 'bg-blue-600 hover:bg-blue-700 focus:ring-blue-500'
              }`}
          >
            {currentTab === 'finalize' ? 'Create News' : 'Next'}
          </button>
        </div>
      </div>

      <LoadingOverlay loading={loading} />
    </BasicLayout>
  );
};

const NewsGenerate: React.FC = () => {
  return (
    <NewsGenerateProvider>
      <NewsGenerateContent />
    </NewsGenerateProvider>
  );
};

export default NewsGenerate;